import { useRolodexEdit } from 'features/fde/rolodexAPI'
import type { LibraWorkflow } from 'features/libra/workflow/workflowStepsApi'
import { useWorkflow } from 'features/libra/WorkflowContext'
import { generateEntrySlug } from 'features/rolodex'
import { useCallback } from 'react'
import type { RolodexEntryPutJson } from 'types'
import { RolodexEntryType } from 'types'
import { v4 } from 'uuid'

import { buildCreateTicketData } from './libraTicketsApi'

/**
 * Shape of the ticket `data` payload accepted by `createLibraTicket`. Mirrors
 * the fields the Zod schema in `libraTicketsApi` accepts at creation.
 */
interface CreateLibraTicketInitialData {
  fieldValues?: Record<string, unknown>
  ticketStatus?: string
}

interface CreateLibraTicketArgs {
  /** From the caller's `useRolodexEdit()`. */
  rolodexPut: (entries: RolodexEntryPutJson[]) => Promise<unknown>
  workflow: LibraWorkflow
  title: string
  organization: string
  site: string
  unit: string
  groupId?: string
  initialData?: CreateLibraTicketInitialData
}

/**
 * Pure / non-hook helper that creates a `libra-ticket` Rolodex entry against
 * the supplied workflow. Useful from creation surfaces that can't depend on
 * the surrounding `WorkflowProvider` — callers obtain `rolodexPut` from their
 * own `useRolodexEdit()`. `useCreateTicket` (below) is the standard
 * wrapper for ticket-creation surfaces inside a `WorkflowProvider`.
 *
 * Delegates the `data` shape to `buildCreateTicketData` (the schema-aware
 * builder in `libraTicketsApi.ts`) so creation defaults stay in one place.
 */
export const createLibraTicket = async ({
  rolodexPut,
  workflow,
  title,
  organization,
  site,
  unit,
  groupId,
  initialData,
}: CreateLibraTicketArgs): Promise<string> => {
  const data = buildCreateTicketData({ workflow, title, initialData })
  const id = generateEntrySlug('ticket', v4().slice(0, 6))
  await rolodexPut([
    {
      id,
      type: RolodexEntryType.LIBRA_TICKET,
      data,
      tags: {
        organization,
        site,
        unit,
        [RolodexEntryType.LIBRA_WORKFLOW]: workflow.id,
        [RolodexEntryType.LIBRA_TICKET_GROUP]: groupId,
      },
    },
  ])
  return id
}

interface UseCreateTicketArgs {
  title: string
  organization: string
  site: string
  unit: string
  groupId?: string
}

/**
 * Hook wrapper around `createLibraTicket` that pulls `rolodexPut` and the
 * active `workflow` from context. Lives next to `createLibraTicket` (and
 * not in `libraTicketsApi.ts`) to avoid a circular import between the two.
 */
export function useCreateTicket(): (
  args: UseCreateTicketArgs
) => Promise<string> {
  const { rolodexPut } = useRolodexEdit()
  const workflow = useWorkflow()
  return useCallback(
    async ({ title, organization, site, unit, groupId }: UseCreateTicketArgs) =>
      createLibraTicket({
        rolodexPut,
        workflow,
        title,
        organization,
        site,
        unit,
        groupId,
      }),
    [rolodexPut, workflow]
  )
}
